import { ChevronDown, ChevronRight } from 'lucide-react';
import { NodeThumb } from '@/components/value-chain/explorer/NodeThumb';
import { shortName } from '@/components/value-chain/nodeVisuals';
import type { TreeIndex } from '@/components/value-chain/treeIndex';
import type { ValueChainNode } from '@/types/valueChain';

type Props = {
  root: ValueChainNode;
  index: TreeIndex;
  expanded: Set<string>;
  focusId: string;
  selectedId: string;
  onToggle: (id: string) => void;
  onSelect: (id: string) => void;
  onExpandAll: () => void;
};

export function HierarchyPanel({
  root,
  index,
  expanded,
  focusId,
  selectedId,
  onToggle,
  onSelect,
  onExpandAll,
}: Props) {
  return (
    <section className="flex min-h-0 min-w-0 flex-1 flex-col rounded-2xl border border-gray-200 bg-white">
      <div className="flex items-center justify-between px-4 py-3">
        <h2 className="text-sm font-semibold text-gray-900">Value Chain Hierarchy</h2>
        <button
          type="button"
          onClick={onExpandAll}
          className="text-[12px] text-gray-400 hover:text-gray-700"
        >
          Expand all
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
        <HierarchyRow
          node={root}
          index={index}
          depth={0}
          expanded={expanded}
          focusId={focusId}
          selectedId={selectedId}
          onToggle={onToggle}
          onSelect={onSelect}
        />
      </div>
    </section>
  );
}

function HierarchyRow({
  node,
  index,
  depth,
  expanded,
  focusId,
  selectedId,
  onToggle,
  onSelect,
}: {
  node: ValueChainNode;
  index: TreeIndex;
  depth: number;
  expanded: Set<string>;
  focusId: string;
  selectedId: string;
  onToggle: (id: string) => void;
  onSelect: (id: string) => void;
}) {
  const childIds = index.children.get(node.id) ?? [];
  const isOpen = expanded.has(node.id);
  const active = node.id === focusId || node.id === selectedId;
  const size = index.subtreeSize.get(node.id) ?? 1;

  return (
    <div>
      <div
        className={`flex items-center gap-1 rounded-xl pr-2 ${
          active ? 'bg-blue-50' : 'hover:bg-gray-50'
        }`}
        style={{ paddingLeft: 8 + depth * 14 }}
      >
        {childIds.length > 0 ? (
          <button
            type="button"
            onClick={() => onToggle(node.id)}
            className="flex h-7 w-5 items-center justify-center text-gray-400"
            aria-label={isOpen ? 'Collapse' : 'Expand'}
          >
            {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </button>
        ) : (
          <span className="w-5" />
        )}
        <button
          type="button"
          onClick={() => onSelect(node.id)}
          className="flex min-w-0 flex-1 items-center gap-2.5 py-2 text-left"
        >
          <NodeThumb node={node} size="sm" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13px] font-medium text-gray-900">
              {shortName(node.name)}
            </span>
            <span className="block truncate text-[11px] text-gray-400">{node.type}</span>
          </span>
          <span className="shrink-0 text-[12px] text-gray-400">{size.toLocaleString()} nodes</span>
        </button>
      </div>
      {isOpen
        ? childIds.map((id) => {
            const child = index.byId.get(id);
            if (!child) return null;
            return (
              <HierarchyRow
                key={id}
                node={child}
                index={index}
                depth={depth + 1}
                expanded={expanded}
                focusId={focusId}
                selectedId={selectedId}
                onToggle={onToggle}
                onSelect={onSelect}
              />
            );
          })
        : null}
    </div>
  );
}
